import {
  alpha,
  Avatar,
  Box,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import HamburgerIcon from "assets/svg/HamburgerIcon";
import theme, { responsiveFontSize } from "themes/Theme";
import { useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { RootState } from "reduxStore/store";

interface IProps {
  toggleDrawer: () => void;
  isMobile: boolean;
}

const Header: React.FC<IProps> = ({ toggleDrawer, isMobile }) => {
  const { profile } = useSelector((state: RootState) => state.adminProfile);
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const userName = useMemo(() => {
    return `${profile?.data?.personal_details?.firstName || ""} ${
      profile?.data?.personal_details?.lastName || ""
    }`;
  }, [profile?.data]);

  const breadCrumbTitle = useMemo(() => {
    const path = pathname;

    const Crumb = ({
      text,
      active = false,
      to,
      fontSize = responsiveFontSize(14),
    }: {
      text: string;
      active?: boolean;
      to?: string;
      fontSize?: string;
    }) => (
      <Typography
        sx={{
          ...(active ? styles.breadCrumbsActive : styles.breadCrumbsDefault),
          fontSize,
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
          maxWidth: 120,
        }}
        onClick={to ? () => navigate(to) : undefined}
      >
        {text}
      </Typography>
    );

    const parts: React.ReactNode[] = [];

    const push = (
      label: string,
      active = false,
      to?: string,
      fontSize?: string
    ) => {
      parts.push(
        <Crumb
          key={label}
          text={label}
          active={active}
          to={to}
          fontSize={fontSize}
        />,
        <Typography key={`${label}-sep`} sx={styles.breadCrumbsDefault}>
          ➠
        </Typography>
      );
    };

    if (path === "/admin/dashboard") {
      return <Crumb text="Dashboard" />;
    }

    push("Dashboard", true, "/admin/dashboard");

    if (path === "/admin/transactions") {
      push("Transactions");
    } else if (path.includes("/admin/transactions/")) {
      push("Transactions", true, "/admin/transactions", responsiveFontSize(12));
      parts.push(
        <Crumb
          key="TransactionDetails"
          text="Transaction Details"
          fontSize={responsiveFontSize(10)}
        />
      );
    } else if (path === "/admin/wallet") {
      push("Wallet");
    } else if (path.includes("/admin/wallet/")) {
      push("Wallet", true, "/admin/wallet");
      parts.push(<Crumb key="WalletDetail" text="Wallet Details" />);
    } else if (path === "/admin/users") {
      push("Users");
    } else if (path === "/admin/user/create") {
      push("Users", true, "/admin/users");
      parts.push(<Crumb key="CreateUser" text="Create User" />);
    } else if (path.includes("/admin/users/")) {
      push("Users", true, "/admin/users");
      parts.push(<Crumb key="UserDetails" text="User Details" />);
    } else if (path === "/admin/settings") {
      push("Settings");
    }

    return (
      <Stack
        direction="row"
        alignItems="center"
        spacing={0.5}
        flexWrap="nowrap"
        sx={{ overflow: "hidden", whiteSpace: "nowrap", minWidth: 0 }}
      >
        {parts}
      </Stack>
    );
  }, [pathname]);

  return (
    <Box sx={styles.container}>
      {/* LEFT: Hamburger + Breadcrumbs */}
      <Stack
        direction="row"
        alignItems="center"
        spacing={1}
        flex={1}
        minWidth={0}
        overflow="hidden"
      >
        {isMobile && (
          <IconButton onClick={toggleDrawer} sx={{ p: 0.5 }}>
            <HamburgerIcon />
          </IconButton>
        )}
        {breadCrumbTitle}
      </Stack>

      {/* RIGHT: Avatar + Name */}
      <Stack
        direction="row"
        alignItems="center"
        spacing={1}
        sx={{ flexShrink: 0, minWidth: 0 }}
      >
        <Avatar
          variant="rounded"
          sx={{
            width: 36,
            height: 36,
            objectFit: "cover",
            borderRadius: "0.75rem",
            flexShrink: 0,
          }}
        />
        <Typography sx={styles.avatarTitle} noWrap>
          {userName}
        </Typography>
      </Stack>
    </Box>
  );
};

export default Header;

const styles = {
  container: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    backgroundColor: theme.palette.primary.main,
    boxShadow:
      "rgba(0, 0, 0, 0.1) 0px 10px 15px -3px, rgba(0, 0, 0, 0.05) 0px 4px 6px -2px",
    py: 1.6,
    px: 2,
    flexWrap: "wrap",
    gap: 1,
  },
  avatarTitle: {
    fontSize: responsiveFontSize(13),
    fontWeight: 400,
    color: theme.palette.common.white,
    maxWidth: 100,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  breadCrumbsDefault: {
    color: alpha(theme.palette.common.white, 0.5),
    fontSize: responsiveFontSize(14),
  },
  breadCrumbsActive: {
    color: theme.palette.common.white,
    fontSize: responsiveFontSize(14),
    fontWeight: 500,
    "&:hover": {
      cursor: "pointer",
    },
  },
};
